import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen, Search, ArrowRight, Rocket, Settings as SettingsIcon, Wrench, CreditCard, Smartphone, ChevronRight,
} from "lucide-react";

import "../styles/marketing.css";
import Seo, { breadcrumbJsonLd } from "../components/marketing/Seo";
import MarketingNav from "../components/marketing/MarketingNav";
import MarketingFooter from "../components/marketing/MarketingFooter";
import Reveal from "../components/marketing/Reveal";

const CATEGORIES = [
  { id: "getting-started", title: "Getting Started", icon: Rocket, desc: "Set up your account, services, phone, team, and book your first job.", articleCount: 5 },
  { id: "features", title: "Features", icon: SettingsIcon, desc: "Deep dive on every feature — scheduling, CRM, invoices, phones, automations.", articleCount: 9 },
  { id: "troubleshooting", title: "Troubleshooting", icon: Wrench, desc: "Quick fixes for the most common issues across calls, jobs, and payments.", articleCount: 13 },
  { id: "billing", title: "Billing & Account", icon: CreditCard, desc: "Manage your plan, billing cycle, cancellation, and account deletion.", articleCount: 5 },
  { id: "mobile", title: "Mobile App", icon: Smartphone, desc: "Install the technician app, learn what's offline-capable, and fix common issues.", articleCount: 4 },
];

const DocsPage = () => {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!query.trim()) return CATEGORIES;
    const q = query.toLowerCase();
    return CATEGORIES.filter((c) => c.title.toLowerCase().includes(q) || c.desc.toLowerCase().includes(q));
  }, [query]);

  return (
    <div className="mk min-h-screen overflow-x-hidden" data-testid="docs-page-root">
      <Seo
        title="Documentation | Fieldz AI Help & Setup Guides"
        description="Official Fieldz AI documentation: getting started, feature guides, troubleshooting, billing, and the technician mobile app — everything to run your field service business."
        path="/docs"
        jsonLd={breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Docs", path: "/docs" }])}
      />
      <MarketingNav />

      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-10 bg-gradient-to-b from-blue-50/70 via-white to-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[var(--mk-blue)] mb-5 flex items-center justify-center gap-1.5">
            <BookOpen className="w-3.5 h-3.5" /> Docs
          </p>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mb-5" data-testid="docs-hub-title">
            Fieldz AI Documentation
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto mb-8">
            Everything you need to set up, run, and grow your field service business. Pick a category below or search to jump straight to what you need.
          </p>
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              placeholder="Search documentation..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="mk-input"
              style={{ padding: "16px 18px 16px 48px", borderRadius: 14 }}
              data-testid="docs-hub-search"
            />
          </div>
        </div>
      </section>

      {/* Category grid */}
      <section className="pb-20">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8">
          {filtered.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((cat, i) => (
                <Reveal key={cat.id} delay={Math.min(i + 1, 5)}>
                  <Link to={`/docs/${cat.id}`} data-testid={`docs-category-${cat.id}`} className="block h-full">
                    <div className="h-full flex flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_16px_32px_-12px_rgba(15,23,42,0.14)] hover:border-blue-200">
                      <div className="flex items-start justify-between mb-5">
                        <div className="w-12 h-12 rounded-xl bg-[var(--mk-blue-light)] flex items-center justify-center">
                          <cat.icon className="w-5 h-5 text-[var(--mk-blue)]" />
                        </div>
                        <span className="text-[11px] uppercase tracking-[0.12em] font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-500">
                          {cat.articleCount} articles
                        </span>
                      </div>
                      <h2 className="text-[20px] font-semibold text-slate-900 mb-2">{cat.title}</h2>
                      <p className="text-[14px] text-slate-500 leading-[1.6] mb-5 flex-1">{cat.desc}</p>
                      <div className="flex items-center gap-1.5 text-[13px] font-semibold text-[var(--mk-blue)]">
                        Browse articles <ChevronRight className="w-4 h-4" />
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-[14px] text-slate-600">No categories match &ldquo;{query}&rdquo;.</p>
              <p className="text-[12px] mt-1 text-slate-400">
                Try a different search or <Link to="/contact" className="text-[var(--mk-blue)] font-semibold hover:underline">contact support</Link>.
              </p>
            </div>
          )}

          <Reveal className="mt-12">
            <div
              className="rounded-2xl border border-blue-200 bg-gradient-to-b from-blue-50/70 to-white p-8 shadow-[0_10px_30px_-8px_rgba(0,70,190,0.12)]"
              data-testid="docs-hub-footer-cta"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
                <div>
                  <h3 className="text-[20px] font-semibold text-slate-900 mb-1">Need help fast?</h3>
                  <p className="text-[14px] text-slate-600">Our team responds within 24 hours and walks you through any setup personally.</p>
                </div>
                <Link to="/contact" className="mk-btn mk-btn-primary mk-btn-lg shrink-0" data-testid="docs-hub-contact-btn">
                  Contact Support <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default DocsPage;
